/**
 * Helpers for building "open in maps" links from address fields.
 *
 * We use Google's documented universal Maps URL, which works across
 * platforms: on Android it opens the Google Maps app when installed, on
 * iOS it opens Google Maps if installed or falls back to the browser
 * (where Safari then offers "Open in Maps" for Apple Maps), and on
 * desktop it opens maps.google.com in the browser. No platform sniffing
 * and no dependency on `geo:` / `maps:` schemes.
 */

export interface DeliveryAddressParts {
  street?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
}

/**
 * Join the non-empty address parts into a single, human-readable string
 * suitable for a maps search query. Empty fields are skipped cleanly so
 * we don't produce strings like `", , Switzerland"`.
 */
export function formatAddressQuery(parts: DeliveryAddressParts): string {
  const street = (parts.street || '').trim();
  const zip = (parts.zip || '').trim();
  const city = (parts.city || '').trim();
  const country = (parts.country || '').trim();

  // Combine zip and city into one segment (e.g. "8001 Zurich") so the
  // resulting query reads naturally.
  const zipCity = [zip, city].filter(Boolean).join(' ');

  return [street, zipCity, country].filter(Boolean).join(', ');
}

/**
 * Whether an address is "openable" — we require at least a street or a
 * city, since zip or country alone wouldn't drop the pin anywhere useful.
 */
export function isOpenableAddress(parts: DeliveryAddressParts): boolean {
  const street = (parts.street || '').trim();
  const city = (parts.city || '').trim();
  return street !== '' || city !== '';
}

/**
 * Build a universal Google Maps search URL for the given address parts,
 * or return `null` if the address is not openable.
 */
export function mapsHref(parts: DeliveryAddressParts): string | null {
  if (!isOpenableAddress(parts)) return null;
  const query = formatAddressQuery(parts);
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
